import React from 'react'
import { ITable } from '../../interfaces'
import { Button, Modal } from "@mui/material";
import FormFields from './FormFields';
import TruncText from '../../misc/TruncText';
import { context } from '../..';
import { Icons } from '../../misc/Icons';

interface ITableProps {
    table: ITable;
    onSubmit: Function
}

export default function Table ({
    table,
    onSubmit = (values) => {}
}: ITableProps) {

    const valueSets = context.answers[table.list_name]
    const maxColumnsToShow = Math.floor(document.body.offsetWidth / 120)

    const [error, setError] = React.useState(null)
    const [rows, setRows] = React.useState(valueSets ? valueSets : [])

    const [rowEditIndex, setRowEditIndex] = React.useState(null)
    const [rowCreate, setRowCreate] = React.useState(false)

    // If columns are not specified, derive them fields
    if (!table.columns) {
        table.columns = {}
        table.fields.forEach(field => {
            table.columns[field.name] = field.label
        })
    }

    const addRow = async (newRow) => {
        setError(null)
        var newList = [...rows, newRow]
        setRows(newList)
        await context.submitPageAnswers({[table.list_name]: newList}, false)
        setRowCreate(false)
    }

    const removeRow = async (index) => {
        setError(null)
        var newList = rows.filter((row, i) => i !== index)
        setRows(newList)
        await context.submitPageAnswers({[table.list_name]: newList}, false)
    }

    const updateRow = async (index, updatedRow) => {
        var newList = []
        rows.forEach((row, i) => {
            newList.push(index === i ? updatedRow : row)
        })
        setRows(newList)
        await context.submitPageAnswers({[table.list_name]: newList}, false)
        setRowEditIndex(null)
    }

    const handleSubmit = () => {
        if (table.min_rows && rows?.length < table.min_rows) {
            return setError(`You must provide at least ${table.min_rows} entr${table.min_rows > 1 ? "ies" : "y"}`)
        }

        if (table.max_rows && rows?.length > table.max_rows) {
            return setError(`You must provide no more than ${table.max_rows} entr${table.max_rows > 1 ? "ies" : "y"}`)
        }
        onSubmit({[table.list_name]: rows})
    }

    if (rowEditIndex !== null) {
        return (
            <RowForm
                table={table}
                row={rows[rowEditIndex]}
                onSubmit={(row) => updateRow(rowEditIndex, row)}
                onCancel={() => setRowEditIndex(null)} />
        )
    }

    if (rowCreate) {
        return (
            <RowForm
                table={table}
                row={{}}
                onSubmit={(row) => addRow(row)}
                onCancel={() => setRowCreate(false)} />
        )
    }

    // Column headers
    var columnHeaders = table.fields.map((field, i) => {
        return {fieldName: field.name, fieldLabel: field.label}
    })

    if (table.columns) {
        columnHeaders = columnHeaders.filter(ch => Object.keys(table.columns).includes(ch.fieldName)).map(ch => {
            return {...ch, fieldLabel: table.columns[ch.fieldName]}
        })
    }

    columnHeaders = columnHeaders.filter((ch, i) => i < maxColumnsToShow)

    return (
        <div className="table-section">
            <div className="table">

                <div className="table-headings">
                    {columnHeaders.map((ch, i) => (
                        <div key={i} className="table-cell">{ch.fieldLabel}</div>
                    ))}
                    <div className="table-cell"></div>
                </div>

                <div className="table-body">
                    {rows.map((row, i) => (
                        <div key={i} className="table-row">
                            {columnHeaders.map((ch, i) => {
                                var displayValue = row[ch.fieldName]
                                // Get display value from listSets
                                var field = table.fields.find(f => f.name === ch.fieldName)
                                if (field?.setListKey) {
                                    var displayDict = context.getListSet(field?.setListKey)
                                    displayValue = displayDict[displayValue] ? displayDict[displayValue] : displayValue
                                } else if (field.options) {
                                    displayValue = field.options[displayValue]
                                } else if (["true", "false"].includes(displayValue)) {
                                    displayValue = displayValue === "true" ? "Yes" : "No"
                                }
                                return (
                                    <div key={i} className="table-cell">
                                        <TruncText text={displayValue} />
                                    </div>
                                )
                            })}

                            <div className="table-cell">
                                <div style={{display: "flex", gap: "5px", marginLeft: "auto"}}>
                                    <Button
                                        className="btn btn-xs"
                                        color="info"
                                        variant="contained"
                                        onClick={() => setRowEditIndex(i)}>
                                        Edit
                                    </Button>

                                    <Button
                                        onClick={() => removeRow(i)}
                                        className="btn btn-xs"
                                        color="error"
                                        variant="contained">
                                        Remove
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}

                    {!rows.length ? (
                        <div className="table-row">
                            <div className="table-cell">
                                No entries
                            </div>
                        </div>
                    ) : null}
                </div>

                <Button
                    onClick={() => setRowCreate(true)}
                    className="btn btn-sm"
                    variant="contained"
                    color="info"
                    style={{marginTop: "10px"}}>
                    <Icons.Plus /> Add new {table.list_item_label}
                </Button>

                {error ? <div className="warning">{error}</div> : null}
            </div>
            
            <div style={{display: "flex", gap: "15px", margin: "30px 0"}}>
                {/* <Button
                    color="info"
                    variant="contained"
                    className="btn btn-lg"
                    onClick={() => context.logout()}>
                    Save and Quit
                </Button> */}

                <Button
                    type="submit"
                    color="success"
                    variant="contained"
                    className="btn btn-lg"
                    disabled={context.locked}
                    onClick={() => handleSubmit()}>
                    Save and Continue
                </Button>
            </div>
        </div>
    )
}


function RowForm ({
    row,
    table,
    onSubmit = async (newRow) => {},
    onCancel = () => {}
}) {

    return (
        <div>
            <Button
                type="submit"
                color="info"
                variant="contained"
                className="btn btn-md"
                style={{margin: "15px 0"}}
                disabled={context.locked}
                onClick={() => onCancel()}>
                <Icons.ChevronLeft /> Back to {table.list_item_label} list
            </Button>

            <FormFields
                initialValues={row}
                fields={table.fields}
                submitButtonText={`Save this ${table.list_item_label}`}
                quitButtonText=""
                // onCancel={() => onCancel()}
                onSubmit={async (values) => {
                    var newRow = {}
                    table.fields.forEach(field => {
                        newRow[field.name] = values[field.name]
                    })
                    await onSubmit(newRow)
            }} />
        </div>
    )
}